import React from 'react';
import { useNavigate } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { Note } from '../types/notes';
import { notesApi } from '../services/api';
import { parseTags } from '../lib/tags';
import { HIGHLIGHTERS, highlighterFor, tiltFor } from '../lib/paper';
import { TagPill, Tape, WritingScribble } from './paper';

/**
 * Inbox / grid note card — the web twin of mobile's NoteCard: a taped-on print
 * of the scan, serif-italic title, summary and highlighter tags, dropped on the
 * desk at a slight tilt. Shows a writing scribble while OCR is pending.
 */
export const NoteCard: React.FC<{
  note: Note;
  onClick?: (noteId: number) => void;
  className?: string;
}> = ({ note, onClick, className = '' }) => {
  const navigate = useNavigate();
  const tags = parseTags(note.tags).slice(0, 3);
  // Tape takes its colour from the first tag, so a note's tape matches its topic.
  const tape = tags[0] ? highlighterFor(tags[0]) : HIGHLIGHTERS.yellow;
  const open = (): void => {
    if (onClick) onClick(note.id);
    else navigate(`/note/${note.id}`);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={open}
      onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), open())}
      className={`pb-squish pb-card flex cursor-pointer gap-4 p-3 pl-3.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-accent ${className}`}
      style={{ '--tilt': `${tiltFor(note.id)}deg` } as React.CSSProperties}
    >
      <div className="relative shrink-0 pt-1">
        {/* A small print of the scan: white border, own shadow, knocked askew. */}
        <div className="-rotate-[2.5deg] rounded-[3px] bg-surface-container-lowest p-[3px] pb-2 shadow-[0_4px_12px_rgba(28,27,25,0.12)]">
          <img
            src={notesApi.getNoteImageUrl(note.id)}
            alt=""
            loading="lazy"
            className="block h-[72px] w-[58px] rounded-[1px] bg-surface-container object-cover"
            onError={(e) => (e.currentTarget.style.visibility = 'hidden')}
          />
        </div>
        <Tape tone={tape} width={34} height={12} rotate={-8} className="left-3.5 top-[-2px]" />
      </div>

      <div className="flex min-w-0 flex-1 flex-col justify-center gap-1">
        {note.processed ? (
          <>
            <p className="m-0 truncate font-headline text-[1.25rem] italic leading-7 text-on-surface">
              {note.title ?? note.filename}
            </p>
            {note.summary && (
              <p className="m-0 line-clamp-2 font-body text-[0.8125rem] leading-[1.35rem] text-on-surface-variant">
                {note.summary}
              </p>
            )}
            {tags.length > 0 && (
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {tags.map((t) => (
                  <TagPill key={t} label={t} />
                ))}
              </div>
            )}
          </>
        ) : note.failed ? (
          <>
            <p className="m-0 truncate font-headline text-[1.25rem] italic leading-7 text-on-surface">
              {note.filename}
            </p>
            <p className="m-0 mt-1 flex items-center gap-1.5 font-body text-xs text-error">
              <AlertCircle size={13} /> Couldn&apos;t read this one. Open it to try again.
            </p>
          </>
        ) : (
          <>
            <WritingScribble width={120} seed={note.id} />
            <p className="m-0 mt-1.5 font-body text-[0.6875rem] font-medium text-accent">
              Reading your handwriting…
            </p>
          </>
        )}
      </div>
    </div>
  );
};
